import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BookOpen, Edit, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const Classes = ({ user }) => {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStudentsDialogOpen, setIsStudentsDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [deletingClass, setDeletingClass] = useState(null);
  const [selectedClassForStudents, setSelectedClassForStudents] = useState(null);
  const [classStudents, setClassStudents] = useState([]);

  const [classForm, setClassForm] = useState({
    name: "",
    grade: "",
    teacher_id: "",
    timetable: {
      Saturday: "",
      Sunday: "",
      Monday: "",
      Tuesday: "",
      Wednesday: "",
    },
  });

  const [editForm, setEditForm] = useState({
    name: "",
    grade: "",
    teacher_id: "",
    timetable: {
      Saturday: "",
      Sunday: "",
      Monday: "",
      Tuesday: "",
      Wednesday: "",
    },
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, studentsRes] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/students`),
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/classes`, classForm);
      toast.success("✅ Class created successfully!");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create class");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/classes/${editingClass.id}`, editForm);
      toast.success("✅ Class updated successfully!");
      setIsEditDialogOpen(false);
      setEditingClass(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update class");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/classes/${deletingClass.id}`);
      toast.success("❌ Class deleted");
      setIsDeleteDialogOpen(false);
      setDeletingClass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete class");
    }
  };

  const openEditDialog = (cls) => {
    setEditingClass(cls);
    const timetable = cls.timetable || {};
    setEditForm({
      name: cls.name,
      grade: cls.grade,
      teacher_id: cls.teacher_id || "",
      timetable: {
        Sunday: timetable.Sunday || "",
        Monday: timetable.Monday || "",
        Tuesday: timetable.Tuesday || "",
        Wednesday: timetable.Wednesday || "",
        Thursday: timetable.Thursday || "",
      },
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (cls) => {
    setDeletingClass(cls);
    setIsDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setClassForm({
      name: "",
      grade: "",
      teacher_id: "",
      timetable: {
        Saturday: "",
        Sunday: "",
        Monday: "",
        Tuesday: "",
        Wednesday: "",
      },
    });
  };

  const getStudentCount = (classId) => {
    return students.filter(s => s.class_id === classId).length;
  };

  const openStudentsDialog = (cls) => {
    setSelectedClassForStudents(cls);
    const classStudentsList = students.filter(s => s.class_id === cls.id);
    setClassStudents(classStudentsList);
    setIsStudentsDialogOpen(true);
  };

  const handleReassignStudent = async (studentId, newClassId) => {
    try {
      await axios.put(`${API}/students/${studentId}`, { class_id: newClassId });
      toast.success("✅ Student reassigned successfully!");
      fetchData();
      // Update the current view
      const updatedStudents = classStudents.filter(s => s.id !== studentId);
      setClassStudents(updatedStudents);
    } catch (error) {
      toast.error("Failed to reassign student");
    }
  };

  const handleRemoveStudent = async (studentId) => {
    if (!window.confirm("Are you sure you want to remove this student from the class?")) {
      return;
    }
    try {
      await axios.put(`${API}/students/${studentId}`, { class_id: "" });
      toast.success("Student removed from class");
      fetchData();
      const updatedStudents = classStudents.filter(s => s.id !== studentId);
      setClassStudents(updatedStudents);
    } catch (error) {
      toast.error("Failed to remove student");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  const timetableDefault = {
    Saturday: "Quran 8:00-10:00, Arabic 10:00-12:00",
    Sunday: "Quran 8:00-10:00, Arabic 10:00-12:00",
    Monday: "Quran 8:00-10:00, Islamic Studies 10:00-12:00",
    Tuesday: "Arabic 8:00-10:00, Quran 10:00-12:00",
    Wednesday: "Quran 8:00-10:00, Arabic 10:00-12:00",
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#1E624A]">Classes</h2>
          <p className="text-sm text-gray-600">Fasallada</p>
        </div>
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-[#1E624A] hover:bg-[#164935]"
            data-testid="add-class-btn"
          >
            <Plus size={20} className="mr-2" />
            Add Class
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.map((cls) => {
          const studentCount = getStudentCount(cls.id);
          return (
            <div
              key={cls.id}
              data-testid={`class-card-${cls.id}`}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all relative group"
            >
              {/* Action buttons - Fixed positioning */}
              {(user?.role === "admin" || user?.role === "super_admin") && (
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(cls)}
                    className="h-8 w-8 p-0 bg-white shadow-sm hover:bg-gray-50"
                    data-testid={`edit-class-${cls.id}-btn`}
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openDeleteDialog(cls)}
                    className="h-8 w-8 p-0 bg-white shadow-sm text-red-600 hover:bg-red-50"
                    data-testid={`delete-class-${cls.id}-btn`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}

              {/* Content with proper padding to avoid icon overlap */}
              <div className="flex items-start gap-4 mb-4 pr-16">
                <div className="w-12 h-12 bg-[#1E624A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen size={24} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">{cls.name}</h3>
                  <p className="text-sm text-gray-600">Grade: {cls.grade}</p>
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      👩‍🎓 {studentCount} Student{studentCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Weekly Timetable
                </h4>
                <div className="space-y-1 text-xs text-[#444]">
                  {Object.entries(cls.timetable || timetableDefault).map(
                    ([day, schedule]) => (
                      <div key={day}>
                        <span className="font-bold">{day}:</span> <span className="text-gray-600">{schedule}</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* View Students Button */}
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => openStudentsDialog(cls)}
                  className="bg-[#1E624A] hover:bg-[#164935] text-white text-xs"
                  data-testid={`view-students-${cls.id}-btn`}
                >
                  👩‍🎓 View Students
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {classes.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          No classes found. Add your first class to get started.
        </div>
      )}

      {/* Add Class Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Add New Class</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-6 flex-1">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">Class Name *</Label>
                  <Input
                    id="name"
                    value={classForm.name}
                    onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                    placeholder="KG1 - Kindergarten 1"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grade" className="text-sm font-medium">Grade *</Label>
                  <Input
                    id="grade"
                    value={classForm.grade}
                    onChange={(e) => setClassForm({ ...classForm, grade: e.target.value })}
                    placeholder="Kindergarten"
                    required
                  />
                </div>
              </div>

              {/* Weekly Timetable Section */}
              <div className="space-y-3 pt-4 border-t">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  🗓️ Weekly Timetable
                </h3>
                <p className="text-xs text-gray-600">Enter schedule for each day (e.g., "Quran 8:00-10:00, Arabic 10:00-12:00")</p>
                
                {Object.keys(classForm.timetable).map((day) => (
                  <div key={day} className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">{day}</Label>
                    <Input
                      value={classForm.timetable[day]}
                      onChange={(e) => setClassForm({
                        ...classForm,
                        timetable: { ...classForm.timetable, [day]: e.target.value }
                      })}
                      placeholder="Quran 8:00-10:00, Arabic 10:00-12:00"
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                Add Class
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Class Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl text-[#1E624A]">Edit Class — {editingClass?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="overflow-y-auto px-6 py-4 space-y-6 flex-1">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_name" className="text-sm font-medium">Class Name *</Label>
                  <Input
                    id="edit_name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_grade" className="text-sm font-medium">Grade *</Label>
                  <Input
                    id="edit_grade"
                    value={editForm.grade}
                    onChange={(e) => setEditForm({ ...editForm, grade: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Weekly Timetable Section */}
              <div className="space-y-3 pt-4 border-t">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  🗓️ Weekly Timetable
                </h3>
                <p className="text-xs text-gray-600">Enter schedule for each day (e.g., "Quran 8:00-10:00, Arabic 10:00-12:00")</p>
                
                {Object.keys(editForm.timetable).map((day) => (
                  <div key={day} className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">{day}</Label>
                    <Input
                      value={editForm.timetable[day]}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        timetable: { ...editForm.timetable, [day]: e.target.value }
                      })}
                      placeholder="Quran 8:00-10:00, Arabic 10:00-12:00"
                      className="text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1E624A] hover:bg-[#164935]">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-600 flex items-center gap-2">
              ⚠️ Delete Class
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-bold">{deletingClass?.name}</span>?
            </p>
            <p className="text-sm text-gray-600 mt-2">
              This will remove the class timetable and unassign students. This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Class
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Students List Modal */}
      <Dialog open={isStudentsDialogOpen} onOpenChange={setIsStudentsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-[#1E624A] to-[#2a8a6a]">
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              👩‍🎓 Students in {selectedClassForStudents?.name}
            </DialogTitle>
            <div className="flex gap-6 mt-3 text-sm text-white/90">
              <div>
                <p className="text-xs text-white/70">Total Students</p>
                <p className="font-bold text-lg">{classStudents.length}</p>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {classStudents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                      {/* Fee Type column only for admin/super_admin */}
                      {(user?.role === "admin" || user?.role === "super_admin") && (
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Fee Type</th>
                      )}
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Session</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Transport</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {classStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm font-medium text-gray-900">
                          {student.name}
                        </td>
                        {/* Fee Type data cell only for admin/super_admin */}
                        {(user?.role === "admin" || user?.role === "super_admin") && (
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                              student.fee_type === "full"
                                ? "bg-green-100 text-green-700"
                                : student.fee_type === "discounted"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-blue-100 text-blue-700"
                            }`}>
                              {student.fee_type === "full" && "💰 Full"}
                              {student.fee_type === "discounted" && `💸 ${student.discount_percentage}% Off`}
                              {student.fee_type === "endorsed" && "🔵 Sponsored"}
                            </span>
                          </td>
                        )}
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                            student.session === "morning" 
                              ? "bg-orange-100 text-orange-700" 
                              : "bg-purple-100 text-purple-700"
                          }`}>
                            {student.session === "morning" ? "☀️ Morning" : "🌙 Afternoon"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                            student.transport_type === "bus"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                          }`}>
                            {student.transport_type === "bus" ? "🚌 Bus" : "👨‍👩‍👧‍👦 Guardian"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex gap-2 justify-center">
                            <Select onValueChange={(value) => handleReassignStudent(student.id, value)}>
                              <SelectTrigger className="h-8 text-xs w-[120px]">
                                <SelectValue placeholder="Reassign" />
                              </SelectTrigger>
                              <SelectContent>
                                {classes.filter(c => c.id !== selectedClassForStudents?.id).map((cls) => (
                                  <SelectItem key={cls.id} value={cls.id}>
                                    {cls.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveStudent(student.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <BookOpen size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="text-lg font-medium">No students enrolled in this class yet</p>
                <p className="text-sm mt-1">Students will appear here when they are assigned to this class</p>
              </div>
            )}
          </div>

          <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setIsStudentsDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Classes;
